/* eslint-disable camelcase */
// --- Request Processors

import { globalState, parseJSON } from '@/components/helpers/utils'
import { getProductPrices } from '../../storefront/products'
import { hydrateAvailability } from 'libs/athos/helpers/availability'
import type {
  AthosResult,
  Facet,
  NormalisedFacet,
  Sorting,
  NormalisedSorting,
  Swatch,
  NormalisedVariant,
  NormalisedImage,
  NormalisedResult,
  NormalisedBlogResult
} from 'libs/athos/athos.types'

export function normaliseFacets(facets: Facet[]): NormalisedFacet[] {
  return facets.map((facet) => {
    return {
      ...facet,
      values: facet.values.filter((item) => item.value !== '"')
    }
  })
}

/**
 * Normalise the sort options from the athos API
 */
export function normaliseSortOptions(options: Sorting[]): NormalisedSorting[] {
  return options.map((option) => {
    return {
      ...option,
      active: option.active ?? 0
    }
  })
}

/**
 * Normalise the sibling swatches metafield from the athos API
 */
export function normaliseSwatches(result: any): Swatch[] {
  const parsedSwatches = parseJSON(result?.mfield_dotapparel21_siblings as string, 'array')
  if (!parsedSwatches) return []
  const publishedSwatches = parsedSwatches.filter((swatch: Swatch) => swatch.published)

  const swatchesWithImages = publishedSwatches.map((swatch: Swatch) => {
    return {
      ...swatch,
      url: result.url
    }
  })
  return swatchesWithImages.sort((swatchA: Swatch, swatchB: Swatch) => {
    if (swatchA.isCurrentProduct === swatchB.isCurrentProduct) return 0
    if (swatchA.isCurrentProduct) return -1
    return 1
  })
}

/**
 * Adapt the Athos Variant-First `variants` object into the flat variant shape the
 * result cards already expect (`variant.id`, `variant.price`, `variant.compare_at_price`,
 * plus option values). This is the lowest-risk migration path: instead of refactoring
 * every consumer to read `variants.data[].mappings.core`, we map Athos → legacy shape
 * once, here.
 *
 * Athos native variant entry shape (per migration guide):
 *   { mappings: { core: { uid, sku, name, price, msrp, url, imageUrl, available } },
 *     options: { size: { value }, color: { value } },
 *     attributes: {...}, optionConfig: {...}, badges: [...] }
 *
 * NOTE: Fields the QuickAdd size selector relies on (`option1`/`option2`/`title`,
 * `inventory_quantity`, `mfield_utopia_*`) are NOT part of the documented native
 * variant object — they must be exposed via the AMC feed (surfaced here through the
 * `attributes`/`core` spread). Validate QuickAdd against a live Athos response during
 * pilot QA and extend the mapping below if the feed names differ.
 *
 * @returns legacy-shaped variants, or null when not an Athos variants object
 */
export function adaptAthosVariants(result: AthosResult): NormalisedVariant[] | null {
  const variants = result?.variants
  const variantData = typeof variants === 'object' && variants !== null ? variants.data : undefined
  if (!Array.isArray(variantData)) return null

  return variantData.map((entry: any) => {
    const core = entry?.mappings?.core ?? {}
    const options = entry?.options ?? {}

    // Flatten `{ size: { value: '11' } }` → `{ size: '11' }` for convenient access,
    // while keeping the raw `options`/`optionConfig` for advanced consumers.
    const flatOptions: Record<string, unknown> = {}
    for (const [key, value] of Object.entries<any>(options)) {
      flatOptions[key] = value?.value ?? value
    }

    return {
      ...entry.attributes,
      ...core,
      ...flatOptions,
      id: core.uid ?? entry.uid,
      price: core.price,
      compare_at_price: core.msrp,
      available: core.available,
      options,
      optionConfig: entry.optionConfig,
      badges: entry.badges
    }
  })
}

/**
 * Synthesise a single product-level variant from a result's top-level fields.
 * Used when the API response carries no variant object at all (the case for the
 * current Athos feed, which returns flat variant rows without a `variants` object).
 * Keeps result cards, B2B pricing and Quick Add functional.
 *
 * NOTE: `price`/`compare_at_price` are only populated if the AMC feed maps them
 * onto the result. If price display or size selection is empty on the grid, the
 * feed's core-field mapping (price, msrp, per-variant options) needs configuring
 * in the Athos Management Console — the frontend can only surface what is returned.
 */
function synthesiseVariant(result: any): NormalisedVariant {
  // Don't collapse "the feed doesn't expose availability here" into a definite
  // `false` — that reads as sold-out everywhere downstream (see useQuickAdd's
  // isVariantOutOfStock) and silently hides Quick Add on any surface (e.g.
  // recommendations) whose feed mapping doesn't carry this field. Only coerce
  // when the source actually has a value; leave it `undefined` otherwise so
  // consumers fall back to other stock signals instead of assuming sold-out.
  const available = result.available === true || result.available === 'true'
    ? true
    : result.available === false || result.available === 'false'
      ? false
      : undefined

  return {
    ...result,
    id: result.uid ?? result.id,
    price: result.price,
    compare_at_price: result.compare_at_price ?? result.msrp,
    available,
    title: result.name
  }
}

/**
 * Adapt Discovery's (`/v1/search`, `/v1/category`) flat `ss_sizes` feed field into
 * the legacy variant shape. Discovery results carry no `variants` object at all —
 * `ss_sizes` is the AMC feed field that closes the gap: an HTML-entity-encoded JSON
 * string, e.g. `[{"label":"6","variant_id":"44812386107415","available":1}, ...]`.
 * `parseJSON` already strips the `&quot;` encoding before parsing.
 */
function adaptSizesFromFeed(result: any): NormalisedVariant[] | null {
  const sizes = parseJSON(result?.ss_sizes, 'array')
  if (!Array.isArray(sizes) || !sizes.length) return null

  return sizes.map((entry: any) => ({
    id: entry.variant_id,
    price: result.price,
    compare_at_price: result.compare_at_price ?? result.msrp,
    available: entry.available === 1 || entry.available === '1' || entry.available === true,
    title: result.name,
    size: entry.label,
    options: { size: { value: entry.label } }
  }))
}

/**
 * Resolve the variants array for a result regardless of API version / feed shape:
 * - Athos native `variants: { data: [...] }` object → adapt it.
 * - Legacy Searchspring `variants` JSON feed string → parse it.
 * - Discovery's flat `ss_sizes` feed field → adapt it into per-size variants.
 * - No variant data at all → synthesise a single product-level variant so the
 *   card can still render.
 */
function resolveVariants(result: any): NormalisedVariant[] {
  const athosVariants = adaptAthosVariants(result)
  if (athosVariants && athosVariants.length) return athosVariants

  const legacyVariants = parseJSON(result.variants, 'array')
  if (Array.isArray(legacyVariants) && legacyVariants.length) return legacyVariants

  const feedSizes = adaptSizesFromFeed(result)
  if (feedSizes && feedSizes.length) return feedSizes

  return [synthesiseVariant(result)]
}

/**
 * Resolve the display images for a result regardless of API version / feed shape:
 * - Legacy Searchspring: delimited `images` / `images_alt` strings.
 * - Athos: discrete `imageUrl` / `parentImageUrl` / `thumbnailImageUrl` fields.
 * Returns an array of `{ src, alt }` compatible with the result cards.
 */
function resolveImages(result: any): NormalisedImage[] {
  if (result.images || result.images_alt) {
    return normaliseImages(result) ?? []
  }

  const srcs = [result.imageUrl, result.parentImageUrl].filter(Boolean)
  const uniqueSrcs = [...new Set(srcs)]
  return uniqueSrcs.map((src) => ({ src, alt: result.name ?? '' }))
}

/**
 * Normalise delimited string/array types into arrays
 */
export function normalisedArray(result: any): any {
  if (result?.includes('|')) {
    return result?.split('|')
  } else if (typeof result === 'string') {
    return [result]
  } else {
    return result
  }
}

/**
 * Normalise the delimited image fields from the athos API
 */
export function normaliseImages(result: any): NormalisedImage[] {
  const { images, images_alt } = result
  const srcs = normalisedArray(images)
  const alts = normalisedArray(images_alt)

  return srcs?.map((src: string, index: number) => {
    return {
      src,
      alt: alts ? alts[index] : ''
    }
  })
}

/**
 * Apply B2B pricing to search results
 */
function applyB2BPricing(results: any[]): any[] {
  // Check if customer is B2B and B2B pricing is available
  const isB2BCustomer = globalState?.customer?.b2b
  const b2bPricing = globalState?.customer?.b2bPricing

  if (!isB2BCustomer || !b2bPricing) {
    return results
  }

  return results.map((result) => {
    const productId = result.uid || result.id?.toString?.() || result.id
    const b2bData = b2bPricing[productId]

    if (b2bData && b2bData.price) {
      const updatedVariants = result.variants.map((variant: NormalisedVariant) => ({
        ...variant,
        price: b2bData.price.toString(),
        compare_at_price: variant.price || result.price
      }))

      const updatedResult = {
        ...result,
        price: b2bData.price.toString(),
        variants: updatedVariants
      }

      return updatedResult
    }

    return result
  })
}

/**
 * Normalise the product results from the athos API
 */
export async function normaliseResults(results?: AthosResult[] | null): Promise<NormalisedResult[]> {
  if (!results) return []
  const normalisedResults = results.map((result) => {
    const swatches = normaliseSwatches(result)
    const images = resolveImages(result)
    const variants = resolveVariants(result)
    return {
      ...result,
      // `ResultImage` uses the `images` array for its hover swap once it holds
      // two paintable images (videos and 3D posters are filtered out there);
      // otherwise it falls back to `image`. Populate `image` so a single Athos
      // `imageUrl` still renders.
      image: result.image ?? result.imageUrl,
      images,
      swatches,
      variants
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []

  // Deliberately not awaited. The feed has no availability-status
  // field, so Coming Soon has to come from the Storefront API — but blocking
  // the grid on it would put a GraphQL round trip in front of every PLP paint.
  // Cards render now; Quick Add withdraws a moment later for the rare
  // pre-release product, and the add path awaits this same request.
  hydrateAvailability(normalisedResults.map((result: any) => result.handle as string | undefined))

  const b2bResults = applyB2BPricing(normalisedResults)

  if (globalState.localization.currency !== globalState.shopify.currency) {
    return await localiseResults(b2bResults)
  }
  return b2bResults
}

async function localiseResults(normalisedResults: any[]): Promise<any[]> {
  const localisedPrices = await getProductPrices(normalisedResults)
  const localisedResults = normalisedResults.map((result) => {
    const priceMatch = localisedPrices?.find((product: any) => {
      return product.id.split('/').pop() === result.uid
    })

    if (priceMatch) {
      const resultVariants = result.variants.map((variant: NormalisedVariant) => {
        return {
          ...variant,
          price: priceMatch.price
        }
      })
      return {
        ...result,
        variants: resultVariants,
        price: priceMatch.price
      }
    } else {
      return result
    }
  })
  return localisedResults
}

/**
 * Normalise blog article results from the Athos API
 */
export async function normaliseBlogResults(results?: AthosResult[] | null): Promise<NormalisedBlogResult[]> {
  if (!results) return []
  return results.map((result: any) => ({
    id: result.uid,
    title: result.name,
    url: result.url,
    excerpt: result.description,
    image: result.thumbnailImageUrl ? { src: result.thumbnailImageUrl, alt: result.name } : null,
    published_at: result.published_at,
    author: result.author,
    tags: result.tags,
    blog_handle: result.blog_handle
  }))
}

/**
 * Normalise recommendation results from the athos P13N API
 */
export async function normaliseRecommendations(results: any): Promise<NormalisedResult[]> {
  const responseId = results?.responseId
  const normalisedResults = results?.results?.map((result: any) => {
    // Flatten the recommend result: `mappings.core` + `attributes` are hoisted to
    // the top level so the result matches the shape the cards expect.
    const merged = {
      ...result,
      ...result.attributes,
      ...result.mappings?.core
    }

    const swatches = normaliseSwatches(result.attributes)
    const variants = resolveVariants(merged)
    const images = resolveImages(merged)

    return {
      ...merged,
      responseId,
      image: merged.image ?? merged.imageUrl,
      images,
      variants,
      swatches
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []

  // See normaliseResults — recommendation carousels render Quick Add too.
  hydrateAvailability(normalisedResults.map((result: any) => result.handle as string | undefined))

  const b2bResults = applyB2BPricing(normalisedResults)

  if (globalState.localization.currency !== globalState.shopify.currency) {
    return await localiseResults(b2bResults)
  }

  return b2bResults
}
